import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { QRCodeService } from '@/services/QRCodeService';
import { LinksService } from '@/services/LinksService';
import { BioLinkService } from '@/services/BioLinkService';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import {
    Box, Button, ColorInput, Divider, Flex, Group, Paper,
    Select, Text, TextInput, Textarea, Title, useMantineTheme, Tabs, ActionIcon, Tooltip, Menu,
    CopyButton, Image, SimpleGrid, Switch, Accordion,
    FileInput, Modal, PasswordInput, rem
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faArrowRight, faArrowUpFromBracket, faCircleInfo, faCirclePlus,
    faCloudArrowUp, faEllipsisVertical, faGear, faGlobe, faLink, faLock, faPlus,
    faCircle,
    faInfo,
    faPalette, faChevronLeft, faChevronRight,
    faInfoCircle, faEye, faEyeSlash, faUpload, faTimes, faFilePdf,
    faArrowUpRightFromSquare,
    faPencil,
    faCheck,
    faClipboardCheck,
    faClipboard,
    faGlobeAsia,
    faUser,
    faShapes,
    faBorderAll,
    faImage,
    faFilePng,
    faFileImage,
    faFileSvg,
    faChevronDown
} from '@fortawesome/pro-regular-svg-icons';
import { useCampaignsStore } from '@/stores/useCampaignsStore';
import { useLinkSetupStore } from '@/stores/useLinkSetupStore';
import { useBioLinkSetupStore } from '@/stores/Bio/useBioLinkSetupStore';
import { LinkCreateTagsSelection } from '@/components/Modals/LinkCreationModal/LinkCreateTagsSelection';
import { appendQrParameter, ensureHttps } from '@/utils/LinkUtility';
import { QRCode } from '@/components/QRCode/QRCode';
import { useQRCode } from '@/hooks/useQRCode';
import { QR_SHAPE_STYLES, QR_BORDER_STYLES, QR_CENTER_STYLES, DEFAULT_LOGOS, QR_FRAME_STYLES, API_URL } from '@/constants/Constants';
import { UtilsService } from '@/services/UtilsService';
import { Carousel } from '@mantine/carousel';
import { QRFrameType } from '@/utils/qrcode/types';
import html2canvas from 'html2canvas';
import { SOCIAL_MEDIA_TYPES } from '@/constants/Constants';
import { Helmet } from 'react-helmet-async';
import InfiniteScrollSelect from '@/components/InfiniteScrollSelect/InfiniteScrollSelect';

interface SelectedLinkType {
    _id: string;
    short_url: string;
    url?: string;  // Optional for bio links
    campaign_id?: string;
    workspace_id?: string;
    domain_attributes?: {
        url: string;
        scheme?: string;
        netloc?: string;
        registered_domain?: string;
    };
    short_url_lowercase?: string;
}

interface FormValues {
    title: string;
    description: string;
    foreground_color: string;
    background_color: string;
    campaign_id: string;
    tags: string[];
    link_id: string;
    bio_link_id: string;
    url: string;
    short_url: string;
    dotStyle: 'square' | 'circle' | 'dot';
    cornerStyle: 'square' | 'circle' | 'dot';
    custom_link_preview_attributes: {
        title: string;
        description: string;
        image: string;
    };
    summary_page_attributes: {
        title: string;
        description: string;
        image: string;
        button_text: string;
    };
    shape_style: 'square' | 'dots' | 'rounded' | 'classy' | 'classy-rounded' | 'extra-rounded';
    border_style: 'square' | 'dot' | 'extra-rounded' | 'rounded' | 'dots' | 'classy' | 'classy-rounded';
    center_style: 'square' | 'dot' | 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded';
    shape_color: string;
    border_color: string;
    center_color: string;
    shape_background_color: string;
    border_background_color: string;
    center_background_color: string;
    logo_image: string;
    logo_margin: number;
    frame_type: QRFrameType;
    frame_color: string;
    frame_text: string;
    frame_text_color: string;
    social_media_type: string | null;
    email_subject: string;
    email_body: string;
    sms_message: string;
    vcard_first_name: string;
    vcard_last_name: string;
    vcard_phone: string;
    vcard_email: string;
    vcard_company: string;
    vcard_job_title: string;
    vcard_street: string;
    vcard_city: string;
    vcard_state: string;
    vcard_zip: string;
    vcard_country: string;
    vcard_website: string;
    wifi_password: string;
    wifi_encryption_type: string;
    pdf_url: string;
}

const IMAGE_MIME_TYPE = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];

export function QRCodeCreateEditPage(): JSX.Element {
    const { id, workspaceId } = useParams<{ id?: string; workspaceId?: string }>();
    const navigate = useNavigate();
    const theme = useMantineTheme();
    const [defaultWorkspace] = useWorkspaceStore(state => [state.defaultWorkspace]);
    const [campaigns] = useCampaignsStore(state => [state.campaigns]);
    const [, setLink, , setOpenedModal] = useLinkSetupStore(state => [state.link, state.setLink, state.openedModal, state.setOpenedModal]);
    const [, setBioLink] = useBioLinkSetupStore(state => [state.bioLink, state.setBioLink]);
    const [loading, setLoading] = useState<boolean>(false);
    const [sourceType, setSourceType] = useState<string>('new');
    const [links, setLinks] = useState<any[]>([]);
    const [bioLinks, setBioLinks] = useState<any[]>([]);
    const [selectedLink, setSelectedLink] = useState<SelectedLinkType | null>(null);
    const [searchValue, setSearchValue] = useState<string>('');
    const [activeFeatureTab, setActiveFeatureTab] = useState<string>('');
    const [isEditSlug, setIsEditSlug] = useState(false);
    const [editShortURL, setEditShortURL] = useState('');
    const [carouselKey, setCarouselKey] = useState(0);
    const [openedAccordion, setOpenedAccordion] = useState<string[]>(['details']); // Default open the first section
    const [isDeepLinkEnabled, setIsDeepLinkEnabled] = useState<boolean>(false);
    const [isUpdatingFormProgrammatically, setIsUpdatingFormProgrammatically] = useState(false);
    const [qrCodeCreated, setQrCodeCreated] = useState(false);
    const [showSlugWarningModal, setShowSlugWarningModal] = useState(false);

    // Filter out CTA campaigns for social media tabs
    const nonCtaCampaigns = campaigns.filter(campaign => !campaign.is_call_to_action_enabled);

    const form = useForm<FormValues>({
        initialValues: {
            title: '',
            description: '',
            foreground_color: '#000000',
            background_color: '#FFFFFF',
            campaign_id: '',
            tags: [] as string[],
            link_id: '',
            bio_link_id: '',
            url: '',
            short_url: '',
            dotStyle: 'square' as const,
            cornerStyle: 'square' as const,
            custom_link_preview_attributes: {
                title: '',
                description: '',
                image: ''
            },
            summary_page_attributes: {
                title: '',
                description: '',
                image: '',
                button_text: ''
            },
            shape_style: 'square',
            border_style: 'square',
            center_style: 'square',
            shape_color: '#000000',
            border_color: '#000000',
            center_color: '#000000',
            shape_background_color: '#FFFFFF',
            border_background_color: '#FFFFFF',
            center_background_color: '#FFFFFF',
            logo_image: '',
            logo_margin: 5,
            frame_type: 'none',
            frame_color: '#000000',
            frame_text: 'SCAN ME',
            frame_text_color: '#FFFFFF',
            social_media_type: null,
            email_subject: '',
            email_body: '',
            sms_message: '',
            vcard_first_name: '',
            vcard_last_name: '',
            vcard_phone: '',
            vcard_email: '',
            vcard_company: '',
            vcard_job_title: '',
            vcard_street: '',
            vcard_city: '',
            vcard_state: '',
            vcard_zip: '',
            vcard_country: '',
            vcard_website: '',
            wifi_password: '',
            wifi_encryption_type: '',
            pdf_url: ''
        },
        validate: {
            title: (value: string) => !value ? 'Title is required' : null,
            url: (value: string) => {
                // Only validate URL for 'new' source type and social media platforms
                const isSocialMedia = SOCIAL_MEDIA_TYPES.map(type => type.value).includes(sourceType);
                if ((sourceType === 'new' || isSocialMedia) && !value) {
                    return 'URL is required';
                }
                return null;
            },
            link_id: (value: string) => {
                // Only validate link_id for 'link' and 'bio' source types
                if ((sourceType === 'link' || sourceType === 'bio') && !value) {
                    return 'Please select a link';
                }
                return null;
            },
        }
    });

    const isEditMode = !!id;

    useEffect(() => {
        if (isEditMode) {
            fetchQRCodeDetails();
        }
        fetchLinks();
        fetchBioLinks();

        // Reset the link store state when navigating to this page
        setOpenedModal(false);

        // Cleanup function to reset state when unmounting
        return () => {
            setOpenedModal(false);
        };
    }, [id]);

    useEffect(() => {
        if (!isEditMode && sourceType === 'new' && campaigns.length > 0 && !form.values.campaign_id) {
            form.setFieldValue('campaign_id', campaigns[0]._id);
        }

        // Enable deep linking for social media platforms, but disable for phone, email, sms, vcard, wifi, pdf, and text
        if (SOCIAL_MEDIA_TYPES.map(type => type.value).includes(sourceType) &&
            !['phone', 'email', 'sms', 'vcard', 'wifi', 'pdf', 'text'].includes(sourceType)) {
            setIsDeepLinkEnabled(true);
        } else {
            setIsDeepLinkEnabled(false);
        }
    }, [campaigns, isEditMode, sourceType, form.values.campaign_id]);

    useEffect(() => {
        setLink({
            tags: form.values.tags || []
        });
    }, [form.values.tags]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sourceType = params.get('source_type');
        const linkId = params.get('link_id');

        if (sourceType) {
            setSourceType(sourceType);

            if (linkId) {
                handleLinkSelect(linkId, sourceType);
            }

            const linkState = useLinkSetupStore.getState();
            const bioLinkState = useBioLinkSetupStore.getState();

            if (sourceType === 'bio' && bioLinkState.bioLink?._id) {
                const bioLink = bioLinkState.bioLink;

                form.setValues({
                    title: '',
                    description: '',
                    foreground_color: '#000000',
                    background_color: '#FFFFFF',
                    link_id: bioLink._id,
                    short_url: bioLink.short_url,
                    custom_link_preview_attributes: {
                        title: bioLink.meta?.title || bioLink.profile_title,
                        description: bioLink.meta?.description || bioLink.bio_description,
                        image: bioLink.meta?.image || bioLink.profile_image
                    },
                    summary_page_attributes: {
                        title: bioLink.profile_title,
                        description: bioLink.bio_description,
                        image: bioLink.profile_image,
                        button_text: 'Visit Bio Link'
                    }
                });

                setSelectedLink({
                    _id: bioLink._id,
                    short_url: bioLink.short_url,
                    workspace_id: bioLink.workspace_id,
                    domain_attributes: bioLink.domain_attributes
                });
                setActiveFeatureTab('customizeSummaryPage');
            } else if (sourceType === 'link' && linkState.link?._id) {
                const link = linkState.link;

                form.setValues({
                    title: '',
                    description: '',
                    foreground_color: '#000000',
                    background_color: '#FFFFFF',
                    campaign_id: link.campaign_id || '',
                    tags: link.tags || [],
                    link_id: link._id,
                    url: link.url || '',
                    short_url: link.short_url,
                    custom_link_preview_attributes: {
                        title: link.meta?.title || link.title || '',
                        description: link.meta?.description || link.description || '',
                        image: link.meta?.image || ''
                    },
                    summary_page_attributes: {
                        title: link.title || '',
                        description: link.description || '',
                        image: '',
                        button_text: 'Visit Link'
                    }
                });

                setSelectedLink({
                    _id: link._id,
                    short_url: link.short_url,
                    url: link.url,
                    campaign_id: link.campaign_id || undefined,
                    workspace_id: link.workspace_id || undefined,
                    domain_attributes: link.domain_attributes
                });

                setSourceType('existing');
            }
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setCarouselKey(prev => prev + 1);
        }, 300);

        return () => clearTimeout(timer);
    }, []);

    const fetchQRCodeDetails = async (): Promise<void> => {
        setLoading(true);
        await new QRCodeService()
            .getById(workspaceId || '', id || '')
            .then((res) => {
                if (res.data.status) {
                    const qrCode = res.data.data;
                    console.log('QR Code:', qrCode);


                    const tags = qrCode.source_type === 'bio'
                        ? qrCode.bio_link?.tags || []
                        : qrCode.link?.tags || [];

                    form.setValues({
                        title: qrCode.title,
                        description: qrCode.description,
                        foreground_color: qrCode.foreground_color,
                        background_color: qrCode.background_color,
                        campaign_id: qrCode.link?.campaign_id || '',
                        tags: tags,
                        link_id: qrCode.source_type === 'bio' ? qrCode.bio_link_id : qrCode.link_id || '',
                        url: qrCode.link?.url || '',
                        short_url: qrCode.link?.short_url || '',
                        custom_link_preview_attributes: qrCode.custom_link_preview_attributes || {},
                        summary_page_attributes: qrCode.summary_page_attributes || {},
                        shape_style: qrCode.shape_style || 'square',
                        border_style: qrCode.border_style || 'square',
                        center_style: qrCode.center_style || 'square',
                        shape_color: qrCode.shape_color || '#000000',
                        border_color: qrCode.border_color || '#000000',
                        center_color: qrCode.center_color || '#000000',
                        shape_background_color: qrCode.shape_background_color || '#FFFFFF',
                        border_background_color: qrCode.border_background_color || '#FFFFFF',
                        center_background_color: qrCode.center_background_color || '#FFFFFF',
                        logo_image: qrCode.logo_image || '',
                        logo_margin: qrCode.logo_margin || 0,
                        frame_type: qrCode.frame_type || 'none',
                        frame_color: qrCode.frame_color || '#000000',
                        frame_text: qrCode.frame_text || 'SCAN ME',
                        frame_text_color: qrCode.frame_text_color || '#FFFFFF',
                        social_media_type: qrCode.social_media_type || null,
                        email_subject: qrCode.email_subject || '',
                        email_body: qrCode.email_body || '',
                        sms_message: qrCode.sms_message || '',
                        vcard_first_name: qrCode.vcard_first_name || '',
                        vcard_last_name: qrCode.vcard_last_name || '',
                        vcard_phone: qrCode.vcard_phone || '',
                        vcard_email: qrCode.vcard_email || '',
                        vcard_company: qrCode.vcard_company || '',
                        vcard_job_title: qrCode.vcard_job_title || '',
                        vcard_street: qrCode.vcard_street || '',
                        vcard_city: qrCode.vcard_city || '',
                        vcard_state: qrCode.vcard_state || '',
                        vcard_zip: qrCode.vcard_zip || '',
                        vcard_country: qrCode.vcard_country || '',
                        vcard_website: qrCode.vcard_website || '',
                        wifi_password: qrCode.wifi_password || '',
                        wifi_encryption_type: qrCode.wifi_encryption_type || '',
                        pdf_url: qrCode.pdf_url || ''
                    });

                    setLink({
                        _id: qrCode._id,
                        tags: tags,
                        campaign_id: qrCode.campaign_id,
                        workspace_id: qrCode.workspace_id,
                        url: qrCode.url,
                        short_url: qrCode.short_url,
                        domain_attributes: qrCode.domain_attributes
                    });

                    // Check if this is a social media QR code by looking for the social_media_type field
                    if (qrCode.social_media_type && SOCIAL_MEDIA_TYPES.map(type => type.value).includes(qrCode.social_media_type)) {
                        // If it has a social media type, use that for the source type
                        setSourceType(qrCode.social_media_type);
                    } else {
                        // Otherwise use the regular source_type
                        setSourceType(qrCode.source_type || 'new');
                    }

                    setSelectedLink(qrCode.source_type === 'bio' ? qrCode.bio_link : qrCode.link);
                }
            })
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    };

    // State for pagination
    const [linksPage, setLinksPage] = useState<number>(1);
    const [bioLinksPage, setBioLinksPage] = useState<number>(1);
    const [hasMoreLinks, setHasMoreLinks] = useState<boolean>(true);
    const [hasMoreBioLinks, setHasMoreBioLinks] = useState<boolean>(true);
    const [isLoadingLinks, setIsLoadingLinks] = useState<boolean>(false);
    const [isLoadingBioLinks, setIsLoadingBioLinks] = useState<boolean>(false);
    const [linkSearchQuery, setLinkSearchQuery] = useState<string>('');
    const [bioLinkSearchQuery, setBioLinkSearchQuery] = useState<string>('');
    const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

    const fetchLinks = async (page: number = 1, append: boolean = false, search: string = ''): Promise<void> => {
        if (isLoadingLinks) return;

        setIsLoadingLinks(true);
        await new LinksService()
            .getSelectOptions(workspaceId || '', search, page, 20)
            .then((res) => {
                if (res.data.status) {
                    const newLinks = res.data.data || [];
                    if (append) {
                        setLinks(prevLinks => [...prevLinks, ...newLinks]);
                    } else {
                        setLinks(newLinks);
                    }

                    // Check if there are more links to load
                    setHasMoreLinks(newLinks.length === 20);
                    setLinksPage(page);
                }
            })
            .catch((err) => console.log(err))
            .finally(() => setIsLoadingLinks(false));
    };

    const fetchBioLinks = async (page: number = 1, append: boolean = false, search: string = ''): Promise<void> => {
        if (isLoadingBioLinks) return;

        setIsLoadingBioLinks(true);
        await new BioLinkService()
            .getSelectOptions(workspaceId || '', search, page, 20)
            .then((res) => {
                if (res.data.status) {
                    const newBioLinks = res.data.data || [];
                    if (append) {
                        setBioLinks(prevBioLinks => [...prevBioLinks, ...newBioLinks]);
                    } else {
                        setBioLinks(newBioLinks);
                    }

                    // Check if there are more bio links to load
                    setHasMoreBioLinks(newBioLinks.length === 20);
                    setBioLinksPage(page);
                }
            })
            .catch((err) => console.log(err))
            .finally(() => setIsLoadingBioLinks(false));
    };

    const handleLinkSearch = (query: string) => {
        setLinkSearchQuery(query);
        setLinksPage(1);
        fetchLinks(1, false, query);
    };

    const handleBioLinkSearch = (query: string) => {
        setBioLinkSearchQuery(query);
        setBioLinksPage(1);
        fetchBioLinks(1, false, query);
    };

    const loadMoreLinks = () => {
        if (hasMoreLinks && !isLoadingLinks) {
            fetchLinks(linksPage + 1, true, linkSearchQuery);
        }
    };

    const loadMoreBioLinks = () => {
        if (hasMoreBioLinks && !isLoadingBioLinks) {
            fetchBioLinks(bioLinksPage + 1, true, bioLinkSearchQuery);
        }
    };

    const handleSubmit = async (values: any): Promise<void> => {
        setLoading(true);
        try {
            const linkState = useLinkSetupStore.getState();

            // Determine the correct source_type based on the current tab
            const isSocialMedia = SOCIAL_MEDIA_TYPES.map(type => type.value).includes(sourceType);
            const actualSourceType = isSocialMedia ? 'new' : sourceType;

            const payload = {
                ...values,
                workspace_id: workspaceId,
                source_type: actualSourceType,
                link_id: sourceType === 'link' ? values.link_id : null,
                bio_link_id: sourceType === 'bio' ? values.link_id : null,
                tags: linkState.link.tags || values.tags || []
            };

            // Ensure campaign_id is included for social media links
            // Add deep linking flags for social media platforms
            if (isSocialMedia) {
                // Use the selected campaign_id or default to the first campaign if none is selected
                payload.campaign_id = values.campaign_id || (campaigns.length > 0 ? campaigns[0]._id : '');
                // Store the original social media platform type in a custom field
                payload.social_media_type = sourceType;

                // Check if the selected campaign is a CTA campaign
                const isCTACampaign = campaigns.find(campaign => campaign._id === payload.campaign_id)?.is_call_to_action_enabled;

                if (isCTACampaign) {
                    // Disable deep linking for CTA campaigns
                    payload.is_deep_link_enabled = false;
                    payload.is_deep_link_allowed = false;
                } else if (isDeepLinkEnabled) {
                    // Enable deep linking for non-CTA campaigns when isDeepLinkEnabled is true
                    payload.is_deep_link_enabled = true;
                    payload.is_deep_link_allowed = true;
                }
            } else if (isDeepLinkEnabled) {
                // For non-social media QR codes, use the original deep linking logic
                payload.is_deep_link_enabled = true;
                payload.is_deep_link_allowed = true;
            }

            if (isEditMode) {
                await new QRCodeService()
                    .update(workspaceId || '', id || '', payload)
                    .then((res) => {
                        if (res.data.status) {
                            notifications.show({
                                color: 'green',
                                message: 'QR Code updated successfully',
                            });
                        }
                    });
            } else {
                await new QRCodeService()
                    .create(workspaceId || '', payload)
                    .then((res) => {
                        if (res.data.status) {
                            notifications.show({
                                color: 'green',
                                message: 'QR Code created successfully',
                            });

                            // Redirect to the setup page with the QR code ID
                            if (res.data.data && res.data.data._id) {
                                navigate(`/qr-codes/${workspaceId}/setup/${res.data.data._id}`);
                            }
                        }
                    });
            }
        } catch (error) {
            console.error('Error submitting form:', error);

            // Check for QR code limit exceeded error
            //@ts-ignore
            if (error.response && error.response.status === 403 && error.response.data && error.response.data.message && error.response.data.message.includes('limit of QR codes')) {
                notifications.show({
                    color: 'red',
                    message: 'You have reached the limit of QR codes allowed in your plan! Please upgrade your plan to create more QR codes.',
                });
            } else {
                notifications.show({
                    color: 'red',
                    message: 'Error saving QR Code',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLinkSelect = async (linkId: string, type: string): Promise<void> => {
        if (!linkId) {
            setSelectedLink(null);
            form.setFieldValue('link_id', '');
            return;
        }

        setLoading(true);
        try {
            let response;
            if (type === 'link') {
                response = await new LinksService().getById(workspaceId || '', linkId);
            } else {
                response = await new BioLinkService().getById(workspaceId || '', linkId);
            }

            if (response.data.status) {
                const link = response.data.data;
                console.log('Selected Link:', link);

                // Check if the link already has a QR code
                if (link.qr_code) {
                    notifications.show({
                        color: 'yellow',
                        message: 'A QR code has already been created for this link',
                    });
                    // Reset selection
                    setSelectedLink(null);
                    form.setFieldValue('link_id', '');
                    form.setFieldValue('destination_url', null);

                    return;
                }

                // Only set values if the link doesn't have a QR code
                setSelectedLink(link);
                form.setFieldValue('link_id', link._id);
                form.setFieldValue('campaign_id', link.campaign_id || '');
                form.setFieldValue('url', link.url || '');
                form.setFieldValue('short_url', link.short_url || '');
                form.setFieldValue('tags', link.tags || []);

                setLink({
                    ...link,
                    tags: link.tags || []
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogoFileDrop = async (files: File[]) => {
        if (files && files.length > 0) {
            const formData = new FormData();
            formData.append('input_file', files[0]);
            setLoading(true);

            try {
                const res = await new UtilsService().uploadImage(formData);

                if (res.data.status) {
                    form.setFieldValue('logo_image', res.data.url);
                    notifications.show({
                        color: 'green',
                        message: 'Logo uploaded successfully',
                    });
                } else {
                    notifications.show({
                        color: 'red',
                        message: 'Failed to upload logo',
                    });
                }
            } catch (error) {
                console.error('Error uploading logo:', error);
                notifications.show({
                    color: 'red',
                    message: 'Error uploading logo',
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle PDF file upload using the same approach as BioCardSection
    const handlePdfFileUpload = async (files: any) => {
        if (files && files.length > 0) {
            const formData = new FormData();
            formData.append('input_file', files[0]);
            setLoading(true);

            await new UtilsService()
                .uploadImage(formData)
                .then((res) => {
                    console.log(res.data);
                    if (res.data.status) {
                        form.setFieldValue('url', res.data.url);
                        handleSocialMediaInputChange(res.data.url);
                        notifications.show({
                            color: 'green',
                            message: 'PDF uploaded successfully',
                        });
                    } else {
                        notifications.show({
                            color: 'red',
                            message: res.data.message || 'Failed to upload PDF',
                        });
                    }
                })
                .catch((err) => {
                    console.error('Error uploading PDF:', err);
                    notifications.show({
                        color: 'red',
                        message: 'Error uploading PDF',
                    });
                });

            setLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            setIsEditSlug(false);
            setEditShortURL('');
        };
    }, []);

    useEffect(() => {
        console.log("QR style updated:", {
            shape_style: form.values.shape_style,
            shape_color: form.values.shape_color,
            shape_background_color: form.values.shape_background_color,
            border_style: form.values.border_style,
            border_color: form.values.border_color,
            border_background_color: form.values.border_background_color,
            center_style: form.values.center_style,
            center_color: form.values.center_color,
            center_background_color: form.values.center_background_color
        });
    }, [
        form.values.shape_style,
        form.values.shape_color,
        form.values.shape_background_color,
        form.values.border_style,
        form.values.border_color,
        form.values.border_background_color,
        form.values.center_style,
        form.values.center_color,
        form.values.center_background_color
    ]);

    const qrConfig = {
        value: appendQrParameter(sourceType === 'bio' ?
            `${selectedLink?.domain_attributes?.url || ''}${selectedLink?.short_url || ''}` :
            sourceType === 'link' ?
                `${selectedLink?.domain_attributes?.url || ''}${selectedLink?.short_url || ''}` :
                form.values.short_url ?
                    `${selectedLink?.domain_attributes?.url || ''}${form.values.short_url}` :
                    ""),
        width: 300,
        height: 300,
        margin: 10,
        dotsOptions: {
            color: form.values.shape_color,
            type: form.values.shape_style as 'square' | 'dots' | 'rounded' | 'classy' | 'classy-rounded' | 'extra-rounded',
            backgroundColor: form.values.shape_background_color
        },
        cornersSquareOptions: {
            color: form.values.border_color,
            type: form.values.border_style as 'square' | 'dot' | 'extra-rounded' | 'rounded' | 'dots' | 'classy' | 'classy-rounded',
            backgroundColor: form.values.border_background_color
        },
        cornersDotOptions: {
            color: form.values.center_color,
            type: form.values.center_style as 'square' | 'dot' | 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded',
            backgroundColor: form.values.center_background_color
        },
        backgroundOptions: {
            color: form.values.shape_background_color
        },
        image: form.values.logo_image || undefined,
        imageOptions: form.values.logo_image ? {
            imageSize: 0.4,
            margin: form.values.logo_margin || 5,
            hideBackgroundDots: form.values.logo_margin === 0,
            crossOrigin: "anonymous"
        } : undefined
    };

    const qrPreviewRef = useRef<HTMLDivElement>(null);

    const downloadQRWithFrame = async (options: { name: string, extension: string, quality: number }) => {
        if (!qrPreviewRef.current) {
            console.error('QR preview reference not found');
            return;
        }

        try {
            // Get the QR code element
            const qrElement = qrPreviewRef.current;

            // Find the actual QR code container (which has the frame and QR code together)
            const qrContainerElement = qrElement.querySelector('.mantine-Paper-root') || qrElement;
            const qrContainer = qrContainerElement as HTMLElement;

            // Use html2canvas to capture the QR code with its frame
            const canvas = await html2canvas(qrContainer, {
                backgroundColor: null, // Transparent background
                scale: 1, // Higher scale for better quality
                logging: false,
                useCORS: true, // Enable CORS for images
                allowTaint: true, // Allow tainted canvas if needed
                width: qrContainer.offsetWidth,
                height: qrContainer.offsetHeight
            });

            // Trim white space from the canvas
            const trimmedCanvas = document.createElement('canvas');
            const trimmedCtx = trimmedCanvas.getContext('2d');

            if (!trimmedCtx) {
                throw new Error('Could not get canvas context');
            }

            // Get the image data to find non-transparent pixels
            const imageData = canvas.getContext('2d')?.getImageData(0, 0, canvas.width, canvas.height);

            if (!imageData) {
                throw new Error('Could not get image data');
            }

            // Find the bounds of non-transparent pixels
            let minX = canvas.width;
            let minY = canvas.height;
            let maxX = 0;
            let maxY = 0;

            const data = imageData.data;
            for (let y = 0; y < canvas.height; y++) {
                for (let x = 0; x < canvas.width; x++) {
                    const alpha = data[((y * canvas.width + x) * 4) + 3];
                    if (alpha > 0) {
                        minX = Math.min(minX, x);
                        minY = Math.min(minY, y);
                        maxX = Math.max(maxX, x);
                        maxY = Math.max(maxY, y);
                    }
                }
            }

            // Add a small padding
            const padding = 5;
            minX = Math.max(0, minX - padding);
            minY = Math.max(0, minY - padding);
            maxX = Math.min(canvas.width, maxX + padding);
            maxY = Math.min(canvas.height, maxY + padding);

            // Set the trimmed canvas size
            const width = maxX - minX;
            const height = maxY - minY;

            // Ensure we have valid dimensions
            if (width <= 0 || height <= 0) {
                throw new Error('Invalid dimensions after trimming');
            }

            trimmedCanvas.width = width;
            trimmedCanvas.height = height;

            // Draw the trimmed image
            trimmedCtx.drawImage(
                canvas,
                minX, minY, width, height,
                0, 0, width, height
            );

            // Convert to data URL and download
            let downloadUrl;
            if (options.extension === 'svg') {
                // For SVG, create a new SVG that includes everything
                const svgData = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
                        <image href="${trimmedCanvas.toDataURL('image/png', options.quality)}" width="${width}" height="${height}" />
                    </svg>
                `;
                const blob = new Blob([svgData], { type: 'image/svg+xml' });
                downloadUrl = URL.createObjectURL(blob);
            } else {
                // For other formats, use canvas toDataURL
                const mimeType = options.extension === 'jpeg' ? 'image/jpeg' :
                    options.extension === 'webp' ? 'image/webp' : 'image/png';
                downloadUrl = trimmedCanvas.toDataURL(mimeType, options.quality);
            }

            // Create download link
            const link = document.createElement('a');
            link.download = `${options.name}.${options.extension}`;
            link.href = downloadUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up
            if (options.extension === 'svg') {
                URL.revokeObjectURL(downloadUrl);
            }
        } catch (error) {
            console.error('Error downloading QR code with frame:', error);
        }
    };

    // Format URL based on social media platform
    const formatSocialMediaUrl = (value: string): string => {
        const platform = SOCIAL_MEDIA_TYPES.find(type => type.value === form.values.social_media_type);
        if (platform && platform.formatUrl) {
            return platform.formatUrl(value);
        }
        return value;
    };

    // Handle social media input change
    const handleSocialMediaInputChange = (value: string) => {
        // Prevent infinite update loops
        if (isUpdatingFormProgrammatically) {
            return;
        }
        console.log('Social media type:', form.values.social_media_type);
        const platform = SOCIAL_MEDIA_TYPES.find(p => p.value === form.values.social_media_type);
        console.log('Platform:', platform);

        if (platform && platform.formatUrl) {
            let formattedUrl = '';

            // Handle platforms with additional fields
            if (platform.hasAdditionalFields) {
                console.log('Has additional fields for platform:', platform);
                if (platform.value === 'email') {
                    formattedUrl = platform.formatUrl(value, {
                        subject: form.values.email_subject,
                        body: form.values.email_body
                    });
                } else if (platform.value === 'sms') {
                    formattedUrl = platform.formatUrl(value, {
                        message: form.values.sms_message
                    });
                } else if (platform.value === 'vcard') {
                    formattedUrl = platform.formatUrl(value, {
                        firstName: form.values.vcard_first_name,
                        lastName: form.values.vcard_last_name,
                        phone: form.values.vcard_phone,
                        email: form.values.vcard_email,
                        company: form.values.vcard_company,
                        jobTitle: form.values.vcard_job_title,
                        street: form.values.vcard_street,
                        city: form.values.vcard_city,
                        state: form.values.vcard_state,
                        zip: form.values.vcard_zip,
                        country: form.values.vcard_country,
                        website: form.values.vcard_website
                    });
                } else if (platform.value === 'wifi') {
                    formattedUrl = platform.formatUrl(value, {
                        password: form.values.wifi_password,
                        encryptionType: form.values.wifi_encryption_type || 'WPA'
                    });
                } else if (platform.value === 'pdf') {
                    formattedUrl = platform.formatUrl(value, {
                        pdfUrl: form.values.pdf_url
                    });
                }
            } else {
                // Standard URL formatting for other platforms
                console.log('Standard URL formatting for platform:', platform);
                formattedUrl = platform.formatUrl(value);
                console.log('Formatted URL:', formattedUrl);
            }

            // Update the URL with the formatted value, but prevent recursive updates
            setIsUpdatingFormProgrammatically(true);
            form.setFieldValue('url', formattedUrl);
            setIsUpdatingFormProgrammatically(false);
        }
    };

    // Page title and meta information
    const pageTitle = id ? 'Edit QR Code' : 'Create QR Code';
    const pageDescription = id
        ? 'Modify your existing QR code with customizable designs, frames, and content types including WiFi, PDF, and text.'
        : 'Create a new QR code with customizable designs, frames, and content types including WiFi, PDF, and text.';

    useEffect(() => {
        return () => {
            // Close the link modal when navigating away from this page
            setOpenedModal(false);
        };
    }, []);

    return (
        <Box>
            <Helmet>
                <title>{pageTitle} | Replug</title>
                <meta name="description" content={pageDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
            </Helmet>

            <Flex gap="md" h="100vh">
                {/* Main Content */}
                <Box style={{ flex: 1, overflow: 'auto' }} p="md">
                    <Flex justify="left" align="center" mb="lg" bg="white" p="md" style={{ borderBottom: `1px solid ${theme.colors.gray[2]}` }}>
                        <Button
                            variant="subtle"
                            leftSection={<FontAwesomeIcon icon={faArrowLeft} />}
                            onClick={() => {
                                // Close the modal before navigating away
                                setOpenedModal(false);
                                navigate(`/qr-codes/${workspaceId}`);
                            }}
                        >
                            Back
                        </Button>

                        {isEditMode && (selectedLink || form.values.url) && (
                            <Group align="center">
                                <Divider orientation="vertical" />
                                <TextInput
                                    w={550}
                                    size="sm"
                                    placeholder="Your QR code URL"
                                    disabled={!isEditSlug}
                                    leftSectionWidth={
                                        selectedLink?.domain_attributes?.url
                                            ? selectedLink.domain_attributes.url.length * 8.1 + 24
                                            : 12 + 24
                                    }
                                    leftSection={
                                        <Flex align={'center'}>
                                            <FontAwesomeIcon icon={faLink} />
                                            <Text ml={12} fz={15}>
                                                {selectedLink?.domain_attributes?.url || ''}
                                            </Text>
                                        </Flex>
                                    }
                                    value={isEditSlug ? editShortURL : (selectedLink ? selectedLink.short_url : form.values.short_url)}
                                    onChange={(event) => {
                                        if (isEditSlug) {
                                            setEditShortURL(event.currentTarget.value);
                                        }
                                    }}
                                    rightSectionWidth={isEditSlug ? 80 : 120}
                                    rightSection={
                                        <>
                                            {isEditSlug ? (
                                                <>
                                                    <Tooltip label="Save">
                                                        <ActionIcon
                                                            variant="subtle"
                                                            color="gray.6"
                                                            onClick={async () => {
                                                                if (sourceType === 'bio') {
                                                                    await new BioLinkService()
                                                                        .update(defaultWorkspace?._id || '', {
                                                                            ...selectedLink,
                                                                            short_url: editShortURL,
                                                                            short_url_lowercase: editShortURL.toLowerCase()
                                                                        })
                                                                        .then((res) => {
                                                                            if (res.data.status) {
                                                                                setSelectedLink(res.data.data);
                                                                                form.setFieldValue('short_url', res.data.data.short_url);
                                                                                setIsEditSlug(false);
                                                                                notifications.show({
                                                                                    color: 'green',
                                                                                    message: 'URL updated successfully',
                                                                                });
                                                                            }
                                                                        })
                                                                        .catch((err) => {
                                                                            notifications.show({
                                                                                color: 'red',
                                                                                message: err.response?.data?.message || 'Error updating URL',
                                                                            });
                                                                        });
                                                                } else {
                                                                    await new LinksService()
                                                                        .editShortenURL(defaultWorkspace?._id || '', {
                                                                            id: selectedLink?._id || '',
                                                                            short_url: editShortURL
                                                                        })
                                                                        .then((res) => {
                                                                            if (res.data.status) {
                                                                                const updatedLink = {
                                                                                    _id: selectedLink?._id || '',
                                                                                    short_url: editShortURL,
                                                                                    url: selectedLink?.url || '',
                                                                                    short_url_lowercase: editShortURL.toLowerCase(),
                                                                                    campaign_id: selectedLink?.campaign_id,
                                                                                    workspace_id: selectedLink?.workspace_id,
                                                                                    domain_attributes: selectedLink?.domain_attributes
                                                                                } as SelectedLinkType;
                                                                                setSelectedLink(updatedLink);
                                                                                form.setFieldValue('short_url', editShortURL);
                                                                                setIsEditSlug(false);
                                                                                notifications.show({
                                                                                    color: 'green',
                                                                                    message: 'URL updated successfully',
                                                                                });
                                                                            }
                                                                        })
                                                                        .catch((err) => {
                                                                            notifications.show({
                                                                                color: 'red',
                                                                                message: err.response?.data?.message || 'Error updating URL',
                                                                            });
                                                                        });
                                                                }
                                                            }}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faCheck}
                                                                style={{
                                                                    color: theme.colors.green[6],
                                                                }}
                                                            />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                    <Tooltip label="Cancel">
                                                        <ActionIcon
                                                            variant="subtle"
                                                            color="gray.6"
                                                            onClick={() => {
                                                                setEditShortURL('');
                                                                setIsEditSlug(false);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faTimes}
                                                                style={{
                                                                    color: theme.colors.red[6],
                                                                }}
                                                            />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Tooltip label="Edit Slug">
                                                        <ActionIcon
                                                            variant="subtle"
                                                            color="gray.6"
                                                            onClick={() => {
                                                                if (qrCodeCreated || isEditMode) {
                                                                    setEditShortURL(selectedLink ? selectedLink.short_url : form.values.short_url);
                                                                    setShowSlugWarningModal(true);
                                                                } else {
                                                                    setEditShortURL(selectedLink ? selectedLink.short_url : form.values.short_url);
                                                                    setIsEditSlug(true);
                                                                }
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faPencil} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                    <Tooltip label="Open link">
                                                        <ActionIcon
                                                            variant="subtle"
                                                            color="gray.6"
                                                            onClick={() => {
                                                                const url = selectedLink?.domain_attributes?.url || '';
                                                                const shortUrl = selectedLink ? selectedLink.short_url : form.values.short_url;
                                                                window.open(`${url}${shortUrl}`, '_blank');
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                                                        </ActionIcon>
                                                    </Tooltip>

                                                    <CopyButton value={`${selectedLink?.domain_attributes?.url || ''}${selectedLink ? selectedLink.short_url : form.values.short_url}`}>
                                                        {({ copied, copy }) => (
                                                            <Tooltip label="Copy link">
                                                                <ActionIcon
                                                                    mr={'lg'}
                                                                    variant="subtle"
                                                                    color={copied ? 'teal' : 'gray.6'}
                                                                    onClick={copy}
                                                                >
                                                                    {copied ? (
                                                                        <FontAwesomeIcon icon={faClipboardCheck} />
                                                                    ) : (
                                                                        <FontAwesomeIcon icon={faClipboard} />
                                                                    )}
                                                                </ActionIcon>
                                                            </Tooltip>
                                                        )}
                                                    </CopyButton>
                                                </>
                                            )}
                                        </>
                                    }
                                />
                            </Group>
                        )}
                    </Flex>

                    <Flex gap="md" wrap="wrap" align="flex-start">
                        {/* Left Section - Form */}
                        <Box style={{ flex: '1 1 calc(66.67% - 8px)', minWidth: '500px' }}>
                            <Accordion
                                multiple
                                value={openedAccordion}
                                onChange={setOpenedAccordion}
                                variant="separated"
                                radius="md"
                                styles={{
                                    item: {
                                        backgroundColor: 'white',
                                        border: `1px solid ${theme.colors.gray[3]}`,
                                        marginBottom: '10px'
                                    },
                                    control: {
                                        backgroundColor: 'white'
                                    },
                                    panel: {
                                        backgroundColor: 'white'
                                    }
                                }}
                            >
                                {/* Details Section */}
                                <Accordion.Item value="details">
                                    <Accordion.Control icon={<FontAwesomeIcon icon={faInfo} />}>
                                        <Title order={5}>Details</Title>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <TextInput
                                            label="Title"
                                            placeholder="Add a title..."
                                            {...form.getInputProps('title')}
                                            mb="md"
                                            autoComplete="off"
                                        />

                                        <Textarea
                                            label="Description"
                                            placeholder="Add a description..."
                                            {...form.getInputProps('description')}
                                            autoComplete="off"
                                        />
                                    </Accordion.Panel>
                                </Accordion.Item>

                                {/* QR Link Section */}
                                <Accordion.Item value="qrlink">
                                    <Accordion.Control icon={<FontAwesomeIcon icon={faLink} />}>
                                        <Title order={5}>QR Code Types</Title>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <Box mb="md">
                                            <Tabs
                                                value={sourceType}
                                                onChange={(value) => {
                                                    if (!isEditMode) {
                                                        setSourceType(value as string);

                                                        // Check if the selected value is a social media type
                                                        const isSocialMediaType = SOCIAL_MEDIA_TYPES.some(type => type.value === value);

                                                        form.setValues({
                                                            ...form.values,
                                                            link_id: '',
                                                            url: '',
                                                            short_url: '',
                                                            campaign_id: value === 'new' ? (campaigns.length > 0 ? campaigns[0]._id : '') : '',
                                                            tags: [],
                                                            // Set social_media_type to the selected value if it's a social media type
                                                            social_media_type: isSocialMediaType ? value as string : null,
                                                            custom_link_preview_attributes: {
                                                                title: '',
                                                                description: '',
                                                                image: ''
                                                            },
                                                            summary_page_attributes: {
                                                                title: '',
                                                                description: '',
                                                                image: '',
                                                                button_text: ''
                                                            }
                                                        });

                                                        setSelectedLink(null);
                                                    }
                                                }}
                                            >
                                                <Box pos="relative" style={{ display: 'flex', alignItems: 'center' }}>
                                                    {/* Previous button */}
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        color="blue"
                                                        style={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            zIndex: 2,
                                                            background: 'white',
                                                            boxShadow: '0 0 5px rgba(0,0,0,0.1)'
                                                        }}
                                                        onClick={() => {
                                                            const container = document.querySelector('.tabs-scroll-container');
                                                            if (container) {
                                                                container.scrollBy({ left: -200, behavior: 'smooth' });
                                                            }
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faChevronLeft} />
                                                    </ActionIcon>

                                                    {/* Scrollable tabs container */}
                                                    <Box
                                                        className="tabs-scroll-container"
                                                        style={{
                                                            overflowX: 'auto',
                                                            scrollbarWidth: 'none', // Hide scrollbar for Firefox
                                                            msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
                                                            '&::-webkit-scrollbar': {
                                                                display: 'none', // Hide scrollbar for Chrome/Safari
                                                            },
                                                            margin: '0 25px', // Space for the buttons
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <Tabs.List
                                                            style={{
                                                                flexWrap: 'nowrap',
                                                                minWidth: 'max-content',
                                                                padding: '0 4px',
                                                            }}
                                                        >
                                                            {/* Website/URL Tab */}
                                                            <Box>
                                                                <Tabs.Tab
                                                                    value="new"
                                                                    disabled={isEditMode && sourceType !== 'new'}
                                                                    style={{
                                                                        padding: '16px 20px',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '15px'
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faGlobeAsia}
                                                                        style={{
                                                                            color: '#003aa9',
                                                                            fontSize: '1.5rem',
                                                                            display: 'block',
                                                                            margin: '0 auto',
                                                                            marginBottom: '10px'
                                                                        }}
                                                                    />
                                                                    <Text size="xs">Website</Text>
                                                                </Tabs.Tab>
                                                            </Box>

                                                            {/* Existing Link Tab */}
                                                            <Box>
                                                                <Tabs.Tab
                                                                    value="link"
                                                                    disabled={isEditMode && sourceType !== 'link'}
                                                                    style={{
                                                                        padding: '16px 20px',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '15px'
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faLink}
                                                                        style={{
                                                                            color: '#003aa9',
                                                                            fontSize: '1.5rem',
                                                                            display: 'block',
                                                                            margin: '0 auto',
                                                                            marginBottom: '10px'
                                                                        }}
                                                                    />
                                                                    <Text size="xs">Link</Text>
                                                                </Tabs.Tab>
                                                            </Box>

                                                            {/* Existing Bio Link Tab */}
                                                            <Box>
                                                                <Tabs.Tab
                                                                    value="bio"
                                                                    disabled={isEditMode && sourceType !== 'bio'}
                                                                    style={{
                                                                        padding: '16px 20px',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: '15px'
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faUser}
                                                                        style={{
                                                                            color: '#003aa9',
                                                                            fontSize: '1.5rem',
                                                                            display: 'block',
                                                                            margin: '0 auto',
                                                                            marginBottom: '10px'
                                                                        }}
                                                                    />
                                                                    <Text size="xs">Bio Link</Text>
                                                                </Tabs.Tab>
                                                            </Box>

                                                            {/* Social Media Tabs */}
                                                            {SOCIAL_MEDIA_TYPES.map((platform) => (
                                                                <Box key={platform.value}>
                                                                    <Tabs.Tab
                                                                        value={platform.value}
                                                                        disabled={isEditMode && sourceType !== platform.value}
                                                                        style={{
                                                                            padding: '12px 20px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            gap: '15px'
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon
                                                                            icon={platform.icon}
                                                                            style={{
                                                                                color: platform.color,
                                                                                fontSize: '1.8rem',
                                                                                display: 'block',
                                                                                margin: '0 auto',
                                                                                marginBottom: '10px'
                                                                            }}
                                                                        />
                                                                        <Text size="xs">{platform.label}</Text>
                                                                    </Tabs.Tab>
                                                                </Box>
                                                            ))}
                                                        </Tabs.List>
                                                    </Box>

                                                    {/* Next button */}
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="subtle"
                                                        color="blue"
                                                        style={{
                                                            position: 'absolute',
                                                            right: 0,
                                                            zIndex: 2,
                                                            background: 'white',
                                                            boxShadow: '0 0 5px rgba(0,0,0,0.1)'
                                                        }}
                                                        onClick={() => {
                                                            const container = document.querySelector('.tabs-scroll-container');
                                                            if (container) {
                                                                container.scrollBy({ left: 200, behavior: 'smooth' });
                                                            }
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faChevronRight} />
                                                    </ActionIcon>
                                                </Box>
                                                <Paper p="lg" withBorder style={{ borderColor: theme.colors.gray[3] }} mt="md">
                                                    <Tabs.Panel value="new">
                                                        <Flex direction="column" gap="md">
                                                            <Flex gap="md">
                                                                <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                    <TextInput
                                                                        label="Destination URL"
                                                                        placeholder="Enter URL..."
                                                                        value={form.values.url}
                                                                        onChange={(event) => {
                                                                            let url = event.currentTarget.value;
                                                                            if (url.length > 0) {
                                                                                url = ensureHttps(url);
                                                                            }
                                                                            form.setFieldValue('url', url);
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Flex>
                                                            <Flex gap="md">
                                                                <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                    <Select
                                                                        label="Campaign"
                                                                        placeholder="Select campaign..."
                                                                        data={campaigns.map(campaign => ({
                                                                            value: campaign._id,
                                                                            label: campaign.name
                                                                        }))}
                                                                        value={form.values.campaign_id || (campaigns.length > 0 && sourceType === 'new' ? campaigns[0]._id : undefined)}
                                                                        onChange={(value) => form.setFieldValue('campaign_id', value || '')}
                                                                    />
                                                                </Box>
                                                                <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                    <LinkCreateTagsSelection />
                                                                </Box>
                                                            </Flex>
                                                        </Flex>
                                                    </Tabs.Panel>

                                                    <Tabs.Panel value="link">
                                                        <Flex direction="column" gap="md">
                                                            <Flex gap="md">
                                                                <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                    <Text size="sm" mb={5}>Select Link</Text>
                                                                    <InfiniteScrollSelect
                                                                        options={links}
                                                                        value={form.values.link_id}
                                                                        onChange={(value) => handleLinkSelect(value || '', 'link')}
                                                                        onSearch={handleLinkSearch}
                                                                        onScrollEnd={loadMoreLinks}
                                                                        isLoading={isLoadingLinks}
                                                                        placeholder="Choose a link..."
                                                                        canClear={true}
                                                                        fullWidth={true}
                                                                    />
                                                                </Box>
                                                                {selectedLink && (
                                                                    <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                        <TextInput
                                                                            label="Destination URL"
                                                                            value={selectedLink.url}
                                                                            onChange={(e) => {
                                                                                // Update the selected link URL
                                                                                setSelectedLink({
                                                                                    ...selectedLink,
                                                                                    url: e.target.value
                                                                                });
                                                                                // Update the form URL value
                                                                                form.setFieldValue('url', e.target.value);
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </Flex>
                                                            <Flex gap="md">
                                                                <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                    <LinkCreateTagsSelection />
                                                                </Box>
                                                                <Box style={{ flex: 1, minWidth: '250px' }} /> {/* Empty box to maintain layout */}
                                                            </Flex>
                                                        </Flex>
                                                    </Tabs.Panel>

                                                    <Tabs.Panel value="bio">
                                                        <Flex gap="md">
                                                            <Box style={{ minWidth: '100%' }}>
                                                                <Text size="sm" mb={5}>Select Bio Link</Text>
                                                                <InfiniteScrollSelect
                                                                    options={bioLinks}
                                                                    value={form.values.link_id}
                                                                    onChange={(value) => {
                                                                        form.setFieldValue('link_id', value || '');
                                                                        handleLinkSelect(value || '', 'bio');
                                                                    }}
                                                                    onSearch={handleBioLinkSearch}
                                                                    onScrollEnd={loadMoreBioLinks}
                                                                    isLoading={isLoadingBioLinks}
                                                                    placeholder="Choose a bio link..."
                                                                    canClear={true}
                                                                    fullWidth={true}
                                                                />
                                                            </Box>
                                                            <Box style={{ flex: 1, minWidth: '250px' }} /> {/* Empty box to maintain layout */}
                                                        </Flex>
                                                    </Tabs.Panel>

                                                    {/* Social Media Panels */}
                                                    {SOCIAL_MEDIA_TYPES.map((platform) => (
                                                        <Tabs.Panel key={platform.value} value={platform.value}>
                                                            <Flex direction="column" gap="md">
                                                                <Flex gap="md">
                                                                    <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                        {!SOCIAL_MEDIA_TYPES.find(p => p.value === platform.value)?.hideUrlField && (
                                                                            <TextInput
                                                                                label={`${platform.label} ${platform.value === 'whatsapp' || platform.value === 'phone' || platform.value === 'sms' ? 'Number' : platform.value === 'email' ? 'Address' : platform.value === 'text' ? '' : 'Username/URL'}`}
                                                                                placeholder={platform.placeholder}
                                                                                value={form.values.url}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('url', e.target.value);
                                                                                    handleSocialMediaInputChange(e.target.value);
                                                                                }}
                                                                                required={!SOCIAL_MEDIA_TYPES.find(p => p.value === platform.value)?.hideUrlField}
                                                                            />
                                                                        )}
                                                                        {platform.value === 'vcard' && (
                                                                            <input
                                                                                type="hidden"
                                                                                value={form.values.vcard_first_name || 'Contact'}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('url', e.target.value);
                                                                                }}
                                                                            />
                                                                        )}
                                                                        {platform.value === 'wifi' && (
                                                                            <input
                                                                                type="hidden"
                                                                                value={form.values.url}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('url', e.target.value);
                                                                                }}
                                                                            />
                                                                        )}
                                                                        {platform.value === 'pdf' && (
                                                                            <input
                                                                                type="hidden"
                                                                                value={form.values.url}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('url', e.target.value);
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                </Flex>

                                                                {platform.value === 'wifi' && (
                                                                    <Flex gap="md">
                                                                        <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                            <TextInput
                                                                                label="SSID"
                                                                                placeholder="Enter WiFi SSID"
                                                                                value={form.values.url}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('url', e.target.value);
                                                                                    if (!isUpdatingFormProgrammatically) {
                                                                                        handleSocialMediaInputChange(e.target.value);
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                        <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                            <PasswordInput
                                                                                label="Password"
                                                                                placeholder="Enter WiFi password"
                                                                                value={form.values.wifi_password}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('wifi_password', e.target.value);
                                                                                    if (!isUpdatingFormProgrammatically) {
                                                                                        handleSocialMediaInputChange(form.values.url);
                                                                                    }
                                                                                }}
                                                                                visibilityToggleIcon={({ reveal }) =>
                                                                                    reveal ?
                                                                                        <FontAwesomeIcon icon={faEyeSlash} style={{ fontSize: '14px' }} /> :
                                                                                        <FontAwesomeIcon icon={faEye} style={{ fontSize: '14px' }} />
                                                                                }
                                                                            />
                                                                        </Box>
                                                                    </Flex>
                                                                )}

                                                                {platform.value === 'wifi' && (
                                                                    <Select
                                                                        mt="md"
                                                                        label="Encryption Type"
                                                                        placeholder="Select encryption type"
                                                                        data={[
                                                                            { value: 'WPA', label: 'WPA/WPA2' },
                                                                            { value: 'WEP', label: 'WEP' },
                                                                            { value: 'nopass', label: 'No Password' }
                                                                        ]}
                                                                        value={form.values.wifi_encryption_type}
                                                                        onChange={(value) => {
                                                                            form.setFieldValue('wifi_encryption_type', value || 'WPA');
                                                                            if (!isUpdatingFormProgrammatically) {
                                                                                handleSocialMediaInputChange(form.values.url);
                                                                            }
                                                                        }}
                                                                    />
                                                                )}

                                                                {platform.value === 'pdf' && (
                                                                    <Flex direction="column" gap="md">

                                                                        <Box>
                                                                            <Text size="sm" w={500} mb="xs">Upload PDF</Text>
                                                                            <Dropzone
                                                                                onDrop={handlePdfFileUpload}
                                                                                onReject={(files) => console.log('rejected files', files)}
                                                                                maxSize={5 * 1024 ** 2}
                                                                                accept={['application/pdf']}
                                                                                loading={loading}
                                                                            >
                                                                                <Group justify="center" gap="xl" mih={100} style={{ pointerEvents: 'none' }}>
                                                                                    <Dropzone.Accept>
                                                                                        <FontAwesomeIcon
                                                                                            icon={faUpload}
                                                                                            style={{
                                                                                                width: rem(32),
                                                                                                height: rem(32),
                                                                                                color: 'var(--mantine-color-blue-6)',
                                                                                            }}
                                                                                        />
                                                                                    </Dropzone.Accept>
                                                                                    <Dropzone.Reject>
                                                                                        <FontAwesomeIcon
                                                                                            icon={faTimes}
                                                                                            style={{
                                                                                                width: rem(32),
                                                                                                height: rem(32),
                                                                                                color: 'var(--mantine-color-red-6)',
                                                                                            }}
                                                                                        />
                                                                                    </Dropzone.Reject>
                                                                                    <Dropzone.Idle>
                                                                                        <FontAwesomeIcon
                                                                                            icon={faFilePdf}
                                                                                            style={{
                                                                                                width: rem(32),
                                                                                                height: rem(32),
                                                                                                color: 'var(--mantine-color-gray-4)',
                                                                                            }}
                                                                                        />
                                                                                    </Dropzone.Idle>

                                                                                    <div>
                                                                                        <Text size="sm" inline>
                                                                                            Drag PDF here or click to select file
                                                                                        </Text>
                                                                                        <Text size="xs" c="dimmed" inline mt={7}>
                                                                                            PDF file should not exceed 5MB
                                                                                        </Text>
                                                                                    </div>
                                                                                </Group>
                                                                            </Dropzone>
                                                                        </Box>

                                                                        <Text size="xs" color="dimmed" mt={5}>
                                                                            Upload a PDF file or enter a URL below
                                                                        </Text>
                                                                        <TextInput
                                                                            label="PDF URL"
                                                                            placeholder="Enter PDF URL or upload below"
                                                                            value={form.values.url}
                                                                            onChange={(e) => {
                                                                                form.setFieldValue('url', e.target.value);
                                                                                handleSocialMediaInputChange(e.target.value);
                                                                            }}
                                                                        />
                                                                    </Flex>
                                                                )}
                                                                {/* Additional fields for Email */}
                                                                {platform.value === 'email' && (
                                                                    <>
                                                                        <Flex gap="md">
                                                                            <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                                <TextInput
                                                                                    label="Subject (Optional)"
                                                                                    placeholder="Enter email subject"
                                                                                    value={form.values.email_subject}
                                                                                    onChange={(e) => {
                                                                                        form.setFieldValue('email_subject', e.target.value);
                                                                                        handleSocialMediaInputChange(form.values.url);
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        </Flex>
                                                                        <Flex gap="md">
                                                                            <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                                <Textarea
                                                                                    label="Body (Optional)"
                                                                                    placeholder="Enter email body"
                                                                                    value={form.values.email_body}
                                                                                    onChange={(e) => {
                                                                                        form.setFieldValue('email_body', e.target.value);
                                                                                        handleSocialMediaInputChange(form.values.url);
                                                                                    }}
                                                                                    minRows={3}
                                                                                />
                                                                            </Box>
                                                                        </Flex>
                                                                    </>
                                                                )}

                                                                {/* Additional fields for SMS */}
                                                                {platform.value === 'sms' && (
                                                                    <Flex gap="md">
                                                                        <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                            <Textarea
                                                                                label="Message (Optional)"
                                                                                placeholder="Enter SMS message"
                                                                                value={form.values.sms_message}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('sms_message', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                                minRows={3}
                                                                            />
                                                                        </Box>
                                                                    </Flex>
                                                                )}

                                                                {/* Additional fields for vCard */}
                                                                {platform.value === 'vcard' && (
                                                                    <>
                                                                        <Divider my="sm" label="Contact Information" labelPosition="center" />
                                                                        <SimpleGrid cols={2}>
                                                                            <TextInput
                                                                                label="First Name"
                                                                                placeholder="Enter first name"
                                                                                value={form.values.vcard_first_name}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('vcard_first_name', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                            />
                                                                            <TextInput
                                                                                label="Last Name"
                                                                                placeholder="Enter last name"
                                                                                value={form.values.vcard_last_name}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('vcard_last_name', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                            />
                                                                        </SimpleGrid>

                                                                        <TextInput
                                                                            mt="md"
                                                                            label="Phone"
                                                                            placeholder="Enter phone number"
                                                                            value={form.values.vcard_phone}
                                                                            onChange={(e) => {
                                                                                form.setFieldValue('vcard_phone', e.target.value);
                                                                                handleSocialMediaInputChange(form.values.url);
                                                                            }}
                                                                        />

                                                                        <SimpleGrid cols={2} mt="md">
                                                                            <TextInput
                                                                                label="Email"
                                                                                placeholder="Enter email address"
                                                                                value={form.values.vcard_email}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('vcard_email', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                            />
                                                                            <TextInput
                                                                                label="Company"
                                                                                placeholder="Enter company name"
                                                                                value={form.values.vcard_company}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('vcard_company', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                            />
                                                                        </SimpleGrid>

                                                                        <SimpleGrid cols={2} mt="md">
                                                                            <TextInput
                                                                                label="Job Title"
                                                                                placeholder="Enter job title"
                                                                                value={form.values.vcard_job_title}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('vcard_job_title', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                            />
                                                                            <TextInput
                                                                                label="Street"
                                                                                placeholder="Enter street address"
                                                                                value={form.values.vcard_street}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('vcard_street', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                            />
                                                                        </SimpleGrid>

                                                                        <SimpleGrid cols={3} mt="md">
                                                                            <TextInput
                                                                                label="City"
                                                                                placeholder="Enter city"
                                                                                value={form.values.vcard_city}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('vcard_city', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                            />
                                                                            <TextInput
                                                                                label="State/Province"
                                                                                placeholder="Enter state/province"
                                                                                value={form.values.vcard_state}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('vcard_state', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                            />
                                                                            <TextInput
                                                                                label="ZIP/Postal Code"
                                                                                placeholder="Enter ZIP/postal code"
                                                                                value={form.values.vcard_zip}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue('vcard_zip', e.target.value);
                                                                                    handleSocialMediaInputChange(form.values.url);
                                                                                }}
                                                                            />
                                                                        </SimpleGrid>

                                                                        <TextInput
                                                                            mt="md"
                                                                            label="Country"
                                                                            placeholder="Enter country"
                                                                            value={form.values.vcard_country}
                                                                            onChange={(e) => {
                                                                                form.setFieldValue('vcard_country', e.target.value);
                                                                                handleSocialMediaInputChange(form.values.url);
                                                                            }}
                                                                        />

                                                                        <TextInput
                                                                            mt="md"
                                                                            label="Website"
                                                                            placeholder="Enter website URL"
                                                                            value={form.values.vcard_website}
                                                                            onChange={(e) => {
                                                                                form.setFieldValue('vcard_website', e.target.value);
                                                                                handleSocialMediaInputChange(form.values.url);
                                                                            }}
                                                                        />
                                                                    </>
                                                                )}

                                                                <Flex gap="md">
                                                                    <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                        <Select
                                                                            label="Campaign"
                                                                            placeholder="Select campaign..."
                                                                            data={campaigns.map(campaign => ({
                                                                                value: campaign._id,
                                                                                label: campaign.name
                                                                            }))}
                                                                            value={form.values.campaign_id || (campaigns.length > 0 ? campaigns[0]._id : undefined)}
                                                                            onChange={(value) => form.setFieldValue('campaign_id', value || '')}
                                                                        />
                                                                        {/* Information text for social media QR blocks */}
                                                                        {SOCIAL_MEDIA_TYPES.map(type => type.value).includes(sourceType) && (
                                                                            <Flex align="center" mt={5}>
                                                                                <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
                                                                                    <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#228be6', marginRight: '6px', fontSize: '12px' }} />QR codes work best with Shortener or Retargeting campaigns. QR codes linked to CTA Or 3rd Party/Custom widget campaigns may not open properly.
                                                                                </Text>
                                                                            </Flex>
                                                                        )}
                                                                    </Box>
                                                                    <Box style={{ flex: 1, minWidth: '250px' }}>
                                                                        <LinkCreateTagsSelection />
                                                                    </Box>
                                                                </Flex>
                                                            </Flex>
                                                        </Tabs.Panel>
                                                    ))}
                                                </Paper>
                                            </Tabs>
                                        </Box>
                                    </Accordion.Panel>
                                </Accordion.Item>

                                {/* QR Code Customization */}
                                <Accordion.Item value="customizations">
                                    <Accordion.Control icon={<FontAwesomeIcon icon={faPalette} />}>
                                        <Title order={5}>Customizations</Title>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <Tabs defaultValue="shape">
                                            <Tabs.List>
                                                <Tabs.Tab
                                                    value="shape"
                                                    leftSection={<FontAwesomeIcon icon={faShapes} style={{ color: '#003aa9' }} />}
                                                >
                                                    Shape
                                                </Tabs.Tab>
                                                <Tabs.Tab
                                                    value="frame"
                                                    leftSection={<FontAwesomeIcon icon={faBorderAll} style={{ color: '#003aa9' }} />}
                                                >
                                                    Frame
                                                </Tabs.Tab>
                                                <Tabs.Tab
                                                    value="logo"
                                                    leftSection={<FontAwesomeIcon icon={faImage} style={{ color: '#003aa9' }} />}
                                                >
                                                    Logo
                                                </Tabs.Tab>
                                            </Tabs.List>

                                            <Paper p="lg" withBorder style={{ borderColor: theme.colors.gray[3] }} mt="md">
                                                <Tabs.Panel value="shape">
                                                    {/* Shape Style Section */}
                                                    <Box mb="lg">
                                                        <Text w={500} size="sm" mb="xs">Shape Style</Text>
                                                        <Group>
                                                            {QR_SHAPE_STYLES.map((style) => (
                                                                <ActionIcon
                                                                    key={style.value}
                                                                    variant="transparent"
                                                                    size="xl"
                                                                    style={{
                                                                        border: form.values.shape_style === style.value ? '2px solid #444444' : '2px solid transparent',
                                                                        borderRadius: '4px',
                                                                        padding: '32px',
                                                                        margin: '10px',
                                                                    }}
                                                                    onClick={() => {
                                                                        console.log('Changing shape style to:', style.value);
                                                                        form.setFieldValue('shape_style', style.value as 'square' | 'dots' | 'rounded' | 'classy' | 'classy-rounded' | 'extra-rounded');
                                                                    }}
                                                                    title={style.label}
                                                                >
                                                                    <div style={{
                                                                        color: form.values.shape_style === style.value ? `${form.values.shape_color}` : `${form.values.shape_color}`,
                                                                    }}>
                                                                        {style.icon}
                                                                    </div>
                                                                </ActionIcon>
                                                            ))}
                                                        </Group>
                                                    </Box>

                                                    {/* Shape Colors */}
                                                    <Box mb="lg" style={{ backgroundColor: '#f2f6f7', padding: '16px', borderRadius: '4px' }}>
                                                        <Group align="flex-start">
                                                            <Box style={{ flex: '0 0 140px' }}>
                                                                <ColorInput
                                                                    label="Dot colour"
                                                                    {...form.getInputProps('shape_color')}
                                                                    onChange={(color) => {
                                                                        console.log('Changing shape color to:', color);
                                                                        form.setFieldValue('shape_color', color);
                                                                    }}
                                                                    size="xs"
                                                                    styles={{
                                                                        label: { fontWeight: 500, marginBottom: 4 },
                                                                        input: { height: '32px', minHeight: '32px' }
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Box style={{ flex: '0 0 140px' }}>
                                                                <ColorInput
                                                                    label="Background colour"
                                                                    {...form.getInputProps('shape_background_color')}
                                                                    onChange={(color) => {
                                                                        console.log('Changing shape background color to:', color);
                                                                        form.setFieldValue('shape_background_color', color);
                                                                    }}
                                                                    size="xs"
                                                                    styles={{
                                                                        label: { fontWeight: 500, marginBottom: 4 },
                                                                        input: { height: '32px', minHeight: '32px' }
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Group>
                                                    </Box>

                                                    {/* Border Style Section */}
                                                    <Box mb="lg">
                                                        <Text w={500} size="sm" mb="xs">Border Style</Text>
                                                        <Group>
                                                            {QR_BORDER_STYLES.map((style) => (
                                                                <ActionIcon
                                                                    key={style.value}
                                                                    variant="transparent"
                                                                    size="xl"
                                                                    style={{
                                                                        border: form.values.border_style === style.value ? '2px solid #444444' : '2px solid transparent',
                                                                        borderRadius: '4px',
                                                                        padding: '32px',
                                                                    }}
                                                                    onClick={() => {
                                                                        console.log('Changing border style to:', style.value);
                                                                        form.setFieldValue('border_style', style.value);
                                                                    }}
                                                                    title={style.label}
                                                                >
                                                                    <div style={{
                                                                        color: form.values.border_style === style.value ? `${form.values.border_color}` : `${form.values.border_color}`
                                                                    }}>
                                                                        {style.icon}
                                                                    </div>
                                                                </ActionIcon>
                                                            ))}
                                                        </Group>
                                                    </Box>

                                                    {/* Border Colors */}
                                                    <Box mb="lg" style={{ backgroundColor: '#f2f6f7', padding: '16px', borderRadius: '4px' }}>
                                                        <Group align="flex-start">
                                                            <Box style={{ flex: '0 0 140px' }}>
                                                                <ColorInput
                                                                    label="Border colour"
                                                                    {...form.getInputProps('border_color')}
                                                                    onChange={(color) => {
                                                                        console.log('Changing border color to:', color);
                                                                        form.setFieldValue('border_color', color);
                                                                    }}
                                                                    size="xs"
                                                                    styles={{
                                                                        label: { fontWeight: 500, marginBottom: 4 },
                                                                        input: { height: '32px', minHeight: '32px' }
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Group>
                                                    </Box>

                                                    {/* Center Style Section */}
                                                    <Box mb="lg">
                                                        <Text w={500} size="sm" mb="xs">Center Style</Text>
                                                        <Group>
                                                            {QR_CENTER_STYLES.map((style) => (
                                                                <ActionIcon
                                                                    key={style.value}
                                                                    variant="transparent"
                                                                    size="xl"
                                                                    style={{
                                                                        border: form.values.center_style === style.value ? '2px solid #444444' : '2px solid transparent',
                                                                        borderRadius: '4px',
                                                                        padding: '32px',
                                                                    }}
                                                                    onClick={() => {
                                                                        console.log('Changing center style to:', style.value);
                                                                        form.setFieldValue('center_style', style.value as 'square' | 'dot' | 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded');
                                                                    }}
                                                                    title={style.label}
                                                                >
                                                                    <div style={{
                                                                        color: form.values.center_style === style.value ? `${form.values.center_color}` : `${form.values.center_color}`
                                                                    }}>
                                                                        {style.icon}
                                                                    </div>
                                                                </ActionIcon>
                                                            ))}
                                                        </Group>
                                                    </Box>

                                                    {/* Center Colors */}
                                                    <Box style={{ backgroundColor: '#f2f6f7', padding: '16px', borderRadius: '4px' }}>
                                                        <Group align="flex-start">
                                                            <Box style={{ flex: '0 0 140px' }}>
                                                                <ColorInput
                                                                    label="Border colour"
                                                                    {...form.getInputProps('center_color')}
                                                                    onChange={(color) => {
                                                                        console.log('Changing center color to:', color);
                                                                        form.setFieldValue('center_color', color);
                                                                    }}
                                                                    size="xs"
                                                                    styles={{
                                                                        label: { fontWeight: 500, marginBottom: 4 },
                                                                        input: { height: '32px', minHeight: '32px' }
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Group>
                                                    </Box>
                                                </Tabs.Panel>

                                                <Tabs.Panel value="frame">
                                                    <Box>
                                                        {/* Frame Type Selection */}
                                                        <Box mb="lg">
                                                            <Text w={500} size="sm" mb="xs">Frame Style</Text>
                                                            <Group>
                                                                {QR_FRAME_STYLES.map((style) => (
                                                                    <Box
                                                                        key={style.value}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            border: form.values.frame_type === style.value ? '2px solid #228be6' : '1px solid #e0e0e0',
                                                                            borderRadius: '8px',
                                                                            padding: '12px',
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            backgroundColor: form.values.frame_type === style.value ? '#e7f5ff' : 'white',
                                                                            transition: 'all 0.2s ease',
                                                                            height: '150px',
                                                                            width: '150px',
                                                                        }}
                                                                        onClick={() => {
                                                                            form.setFieldValue('frame_type', style.value as QRFrameType);
                                                                        }}
                                                                    >
                                                                        {style.icon}
                                                                        <Text size="xs" ta="center" mt="xs">{style.label}</Text>
                                                                    </Box>
                                                                ))}
                                                            </Group>
                                                        </Box>

                                                        {/* Frame Color */}
                                                        {form.values.frame_type !== 'none' && (
                                                            <Box mb="lg" style={{ backgroundColor: '#f2f6f7', padding: '16px', borderRadius: '4px' }}>
                                                                <Group align="flex-start">
                                                                    <Box style={{ flex: '0 0 140px' }}>
                                                                        <ColorInput
                                                                            label="Frame colour"
                                                                            value={form.values.frame_color}
                                                                            onChange={(color) => form.setFieldValue('frame_color', color)}
                                                                            size="xs"
                                                                            styles={{
                                                                                label: { fontWeight: 500, marginBottom: 4 },
                                                                                input: { height: '32px', minHeight: '32px' }
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <Box style={{ flex: '0 0 140px' }}>
                                                                        <ColorInput
                                                                            label="Text colour"
                                                                            value={form.values.frame_text_color}
                                                                            onChange={(color) => form.setFieldValue('frame_text_color', color)}
                                                                            size="xs"
                                                                            styles={{
                                                                                label: { fontWeight: 500, marginBottom: 4 },
                                                                                input: { height: '32px', minHeight: '32px' }
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </Group>
                                                            </Box>
                                                        )}

                                                        {/* Frame Text */}
                                                        {form.values.frame_type !== 'none' && (
                                                            <Box mb="lg" style={{ backgroundColor: '#f2f6f7', padding: '16px', borderRadius: '4px' }}>
                                                                <Text w={500} size="sm" mb="xs">Frame Text</Text>
                                                                <TextInput
                                                                    value={form.values.frame_text}
                                                                    onChange={(e) => {
                                                                        // Limit to 20 characters
                                                                        const text = e.currentTarget.value.slice(0, 20);
                                                                        form.setFieldValue('frame_text', text);
                                                                    }}
                                                                    placeholder="SCAN ME"
                                                                    size="xs"
                                                                    maxLength={20}
                                                                    rightSection={
                                                                        <Text size="xs" color="dimmed" style={{ whiteSpace: 'nowrap', paddingRight: '10px' }}>
                                                                            {form.values.frame_text?.length || 0}/20
                                                                        </Text>
                                                                    }
                                                                    styles={{
                                                                        label: { fontWeight: 500, marginBottom: 4 },
                                                                        input: { height: '32px', minHeight: '32px' }
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Tabs.Panel>

                                                <Tabs.Panel value="logo">
                                                    <Box>
                                                        {/* Logo Upload Section */}
                                                        <Box mb="lg">
                                                            <Text w={500} size="sm" mb="xs">Upload Custom Logo</Text>
                                                            <Dropzone
                                                                onDrop={handleLogoFileDrop}
                                                                onReject={(files) => console.log('rejected files', files)}
                                                                maxSize={3 * 1024 * 1024}
                                                                accept={IMAGE_MIME_TYPE}
                                                                loading={loading}
                                                            >
                                                                <Group justify="center" gap="xl" style={{ minHeight: 50, pointerEvents: 'none' }}>
                                                                    <Dropzone.Accept>
                                                                        <FontAwesomeIcon
                                                                            icon={faUpload}
                                                                            size="2x"
                                                                            style={{ color: 'var(--mantine-color-blue-6)' }}
                                                                        />
                                                                    </Dropzone.Accept>
                                                                    <Dropzone.Reject>
                                                                        <FontAwesomeIcon
                                                                            icon={faTimes}
                                                                            size="2x"
                                                                            style={{ color: 'var(--mantine-color-red-6)' }}
                                                                        />
                                                                    </Dropzone.Reject>
                                                                    <Dropzone.Idle>
                                                                        <FontAwesomeIcon
                                                                            icon={faImage}
                                                                            size="2x"
                                                                            style={{ color: 'var(--mantine-color-dimmed)' }}
                                                                        />
                                                                    </Dropzone.Idle>

                                                                    <div>
                                                                        <Text size="xl" inline>
                                                                            Drag images here or click to select files
                                                                        </Text>
                                                                        <Text size="sm" c="dimmed" inline mt={7}>
                                                                            Attach one image, file should not exceed 3mb
                                                                        </Text>
                                                                    </div>
                                                                </Group>
                                                            </Dropzone>
                                                        </Box>

                                                        {/* Default Logo Options */}
                                                        <Box mb="lg">
                                                            <Text w={500} size="sm" mb="xs" fw={500}>Or choose from here</Text>
                                                            <Box
                                                                style={{
                                                                    maxHeight: '190px',
                                                                    overflowY: 'auto',
                                                                    padding: '12px',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: '#f2f6f7',
                                                                    border: '1px solid #e0e0e0',
                                                                    scrollbarWidth: 'thin',
                                                                    scrollbarColor: '#c1c1c1 #f9f9f9',
                                                                    '&::-webkit-scrollbar': {
                                                                        width: '8px',
                                                                    },
                                                                    '&::-webkit-scrollbar-track': {
                                                                        background: '#f9f9f9',
                                                                        borderRadius: '4px',
                                                                    },
                                                                    '&::-webkit-scrollbar-thumb': {
                                                                        background: '#c1c1c1',
                                                                        borderRadius: '4px',
                                                                        '&:hover': {
                                                                            background: '#a8a8a8',
                                                                        },
                                                                    }
                                                                }}
                                                            >
                                                                <SimpleGrid cols={{ base: 5, sm: 6, md: 8, lg: 10 }} spacing="xs">
                                                                    {DEFAULT_LOGOS.map((logo) => (
                                                                        <Box
                                                                            key={logo.name}
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                border: form.values.logo_image === logo.url ? '2px solid #228be6' : '1px solid #e0e0e0',
                                                                                borderRadius: '8px',
                                                                                padding: '10px',
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                backgroundColor: form.values.logo_image === logo.url ? '#e7f5ff' : 'white',
                                                                                transition: 'all 0.2s ease',
                                                                                height: '80px',
                                                                                boxShadow: form.values.logo_image === logo.url ? '0 2px 5px rgba(34, 139, 230, 0.2)' : 'none',
                                                                                ':hover': {
                                                                                    borderColor: '#228be6',
                                                                                    backgroundColor: '#f1f9ff',
                                                                                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
                                                                                }
                                                                            }}
                                                                            onClick={() => {
                                                                                form.setFieldValue('logo_image', logo.url);
                                                                                // Set default logo size if not already set
                                                                                if (!form.values.logo_margin) form.setFieldValue('logo_margin', 5);
                                                                            }}
                                                                        >
                                                                            <Image
                                                                                src={logo.url}
                                                                                alt={logo.name}
                                                                                width={36}
                                                                                height={36}
                                                                                fit="contain"
                                                                                style={{ marginBottom: '4px' }}
                                                                            />
                                                                            <Text size="xs" ta="center" lineClamp={1}>{logo.name}</Text>
                                                                        </Box>
                                                                    ))}
                                                                </SimpleGrid>
                                                            </Box>
                                                        </Box>

                                                        {/* Logo Background Settings */}
                                                        <Box mb="lg" style={{ backgroundColor: '#f2f6f7', padding: '16px', borderRadius: '4px' }}>
                                                            <Flex align="center" justify="space-between">
                                                                <Text size="sm" fw={500}>Remove background behind logo</Text>
                                                                <Switch
                                                                    checked={form.values.logo_margin === 0}
                                                                    onChange={(event) => {
                                                                        const isChecked = event.currentTarget.checked;
                                                                        form.setFieldValue('logo_margin', isChecked ? 0 : 5);

                                                                        // Update QR code with new settings
                                                                        qrConfig.imageOptions = {
                                                                            imageSize: 0.4,
                                                                            margin: isChecked ? 5 : 0,
                                                                            hideBackgroundDots: isChecked,
                                                                            crossOrigin: "anonymous"
                                                                        };

                                                                        // Update QR code component
                                                                        qrConfig.value = appendQrParameter(sourceType === 'bio' ?
                                                                            `${selectedLink?.domain_attributes?.url || 'http://localhost:3001/'}${selectedLink?.short_url || ''}` :
                                                                            sourceType === 'link' ?
                                                                                `${selectedLink?.domain_attributes?.url || 'http://localhost:3001/'}${selectedLink?.short_url || ''}` :
                                                                                form.values.short_url ?
                                                                                    `${selectedLink?.domain_attributes?.url || 'http://localhost:3001/'}${form.values.short_url}` :
                                                                                    "");
                                                                    }}
                                                                    color="#003aa9"
                                                                />
                                                            </Flex>
                                                        </Box>

                                                        {/* Logo Preview */}
                                                        {form.values.logo_image && (
                                                            <Box mb="lg">
                                                                <Text w={500} size="sm" mb="xs">Logo Preview</Text>
                                                                <Flex align="center" gap="md">
                                                                    <Image
                                                                        src={form.values.logo_image}
                                                                        alt="Logo Preview"
                                                                        width={80}
                                                                        height={80}
                                                                        fit="contain"
                                                                        onError={() => {
                                                                            console.error("Error loading logo image");
                                                                            notifications.show({
                                                                                color: 'red',
                                                                                message: 'Failed to load logo image. Please try another image.',
                                                                            });
                                                                            // Clear the logo image to prevent QR code from disappearing
                                                                            form.setFieldValue('logo_image', '');
                                                                        }}
                                                                    />
                                                                    <Button
                                                                        leftSection={<FontAwesomeIcon icon={faTimes} />}
                                                                        variant="light"
                                                                        color="red"
                                                                        onClick={() => form.setFieldValue('logo_image', '')}
                                                                    >
                                                                        Remove Logo
                                                                    </Button>
                                                                </Flex>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Tabs.Panel>
                                            </Paper>
                                        </Tabs>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            </Accordion>
                        </Box>

                        {/* Right Section - Preview */}
                        <Box style={{
                            flex: '1 1 calc(33.33% - 8px)',
                            minWidth: '300px',
                            position: 'sticky',
                            top: '20px',
                            alignSelf: 'flex-start',
                            maxHeight: 'calc(100vh - 40px)',
                            overflow: 'auto'
                        }}>
                            <Paper p="md" radius="md" withBorder pt="xl">
                                <Title order={4} mb="md" style={{ textAlign: 'center' }}>QR Code Preview</Title>
                                <Box mb="md" style={{ display: 'flex', justifyContent: 'center' }}>
                                    <Paper
                                        radius={form.values.frame_type === 'square' ? 0 : "lg"}
                                        style={{
                                            position: 'relative',
                                            backgroundColor: form.values.background_color || '#FFFFFF',
                                            borderRadius: form.values.frame_type === 'square' ? '0px' : '8px',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
                                        }}
                                        ref={qrPreviewRef}
                                    >
                                        <QRCode
                                            key={`qr-${form.values.shape_style}-${form.values.border_style}-${form.values.center_style}-${form.values.shape_color}-${form.values.border_color}-${form.values.center_color}-${form.values.shape_background_color}-${form.values.border_background_color}-${form.values.center_background_color}-${form.values.logo_image || 'no-logo'}-${form.values.frame_type || 'no-frame'}-${form.values.frame_color}-${form.values.frame_text}`}
                                            value={qrConfig.value}
                                            width={qrConfig.width}
                                            height={qrConfig.height}
                                            margin={qrConfig.margin}
                                            dotsOptions={{
                                                color: form.values.shape_color,
                                                type: form.values.shape_style as 'square' | 'dots' | 'rounded' | 'classy' | 'classy-rounded' | 'extra-rounded',
                                                backgroundColor: form.values.shape_background_color
                                            }}
                                            cornersSquareOptions={{
                                                color: form.values.border_color,
                                                type: form.values.border_style as 'square' | 'dot' | 'extra-rounded' | 'rounded' | 'dots' | 'classy' | 'classy-rounded',
                                                backgroundColor: form.values.border_background_color
                                            }}
                                            cornersDotOptions={{
                                                color: form.values.center_color,
                                                type: form.values.center_style as 'square' | 'dot' | 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded',
                                                backgroundColor: form.values.center_background_color
                                            }}
                                            backgroundOptions={{
                                                color: form.values.shape_background_color
                                            }}
                                            image={form.values.logo_image || undefined}
                                            imageOptions={qrConfig.imageOptions}
                                            loading={loading}
                                            frameType={form.values.frame_type}
                                            frameColor={form.values.frame_color}
                                            frameText={form.values.frame_text}
                                            frameTextColor={form.values.frame_text_color}
                                        />
                                    </Paper>
                                </Box>

                                {/* Action Buttons */}
                                <Box style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '1rem' }}>
                                    <Group align="center">
                                        <Button
                                            variant="filled"
                                            color="blue"
                                            loading={loading}
                                            onClick={() => form.onSubmit(handleSubmit)()}
                                        >
                                            {isEditMode ? 'Update QR Code' : 'Create QR Code'}
                                        </Button>
                                        {isEditMode && <Menu>
                                            <Menu.Target>
                                                <Button
                                                    variant="light"
                                                    rightSection={downloadLoading ? null : <FontAwesomeIcon icon={faChevronDown} />}
                                                    loading={downloadLoading}
                                                >
                                                    Download
                                                </Button>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                                <Menu.Item
                                                    leftSection={<FontAwesomeIcon icon={faFileSvg} />}
                                                    onClick={async () => {
                                                        setDownloadLoading(true);
                                                        await downloadQRWithFrame({
                                                            name: form.values.title || 'qr-code',
                                                            extension: 'svg',
                                                            quality: 1
                                                        });
                                                        setDownloadLoading(false);
                                                    }}
                                                >
                                                    Download as SVG
                                                </Menu.Item>
                                                <Menu.Item
                                                    leftSection={<FontAwesomeIcon icon={faFilePng} />}
                                                    onClick={async () => {
                                                        setDownloadLoading(true);
                                                        await downloadQRWithFrame({
                                                            name: form.values.title || 'qr-code',
                                                            extension: 'png',
                                                            quality: 1
                                                        });
                                                        setDownloadLoading(false);
                                                    }}
                                                >
                                                    Download as PNG
                                                </Menu.Item>
                                                <Menu.Item
                                                    leftSection={<FontAwesomeIcon icon={faFileImage} />}
                                                    onClick={async () => {
                                                        setDownloadLoading(true);
                                                        await downloadQRWithFrame({
                                                            name: form.values.title || 'qr-code',
                                                            extension: 'jpeg',
                                                            quality: 1
                                                        });
                                                        setDownloadLoading(false);
                                                    }}
                                                >
                                                    Download as JPEG
                                                </Menu.Item>
                                                <Menu.Item
                                                    leftSection={<FontAwesomeIcon icon={faFileImage} />}
                                                    onClick={async () => {
                                                        setDownloadLoading(true);
                                                        await downloadQRWithFrame({
                                                            name: form.values.title || 'qr-code',
                                                            extension: 'webp',
                                                            quality: 1
                                                        });
                                                        setDownloadLoading(false);
                                                    }}
                                                >
                                                    Download as WEBP
                                                </Menu.Item>
                                            </Menu.Dropdown>
                                        </Menu>}

                                    </Group>
                                </Box>
                            </Paper>
                        </Box>
                    </Flex>
                </Box>
            </Flex>

            {/* Warning Modal for Slug Edit */}
            <Modal
                opened={showSlugWarningModal}
                onClose={() => setShowSlugWarningModal(false)}
                title={<Text fw={600} size="lg" c="red.6">Important note</Text>}
                centered
            >
                <Text mb="md">
                    Changes to the short URL of an already published QR Code will cause the previous version of the QR Code to be overwritten and it will no longer be scannable.
                </Text>
                <Flex justify="flex-end" gap="md">
                    <Button variant="outline" onClick={() => setShowSlugWarningModal(false)}>
                        CANCEL
                    </Button>
                    <Button
                        color="blue"
                        onClick={() => {
                            setShowSlugWarningModal(false);
                            setIsEditSlug(true);
                        }}
                    >
                        GOT IT
                    </Button>
                </Flex>
            </Modal>
        </Box>
    );
}